import React, { useEffect, useState } from 'react'; 
import { useOutletContext } from 'react-router-dom';
import { useTheme } from 'next-themes';
import { AgentType } from '../../types';
import { 
    MagnifyingGlassIcon, 
    MegaphoneIcon, 
    AcademicCapIcon,
    SquaresPlusIcon,
    ArrowPathIcon,
    PresentationChartBarIcon
} from '@heroicons/react/24/outline';

interface OutletContext {
  onSelectAgent: (agentId: AgentType) => void;
}

const quickAccessAgents = [
    { id: AgentType.WORD_ANALYZER, name: 'Analisar Palavras', icon: <MagnifyingGlassIcon className="w-6 h-6"/>, description: 'Analise termos originais.' },
    { id: AgentType.SERMON_BUILDER, name: 'Construir Sermão', icon: <MegaphoneIcon className="w-6 h-6"/>, description: 'Construa sermões sólidos.' },
    { id: AgentType.BIBLE_STUDY_CREATOR, name: 'Criar Estudo Bíblico', icon: <AcademicCapIcon className="w-6 h-6"/>, description: 'Crie estudos bíblicos.' },
    { id: AgentType.EXPLORE_THEMES, name: 'Explorar Temas', icon: <SquaresPlusIcon className="w-6 h-6"/>, description: 'Explore temas bíblicos.' },
    { id: AgentType.TEXT_UPDATER, name: 'Atualizar Texto', icon: <ArrowPathIcon className="w-6 h-6"/>, description: 'Atualize textos bíblico.' },
    { id: AgentType.LESSON_PREPARER, name: 'Preparar Aula', icon: <PresentationChartBarIcon className="w-6 h-6"/>, description: 'Crie planos de aulas.' },
];


const WelcomeView: React.FC = () => {
  const { onSelectAgent } = useOutletContext<OutletContext>();
  const { theme, systemTheme } = useTheme();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const isLightMode = mounted && (theme === 'system' ? systemTheme !== 'dark' : theme === 'light');
  const logoSrc = isLightMode
    ? '/imgs/logos/EscriturIA-logo-horizontal-sem-slogan.png'
    : '/imgs/logos/EscriturIA-logo-horizontal.png';

  return (
    <div className="flex flex-col items-center h-full p-4 md:p-6 text-center text-foreground bg-background rounded-lg shadow-xl">
      <div className="mb-3 md:mb-4">
        <img src={logoSrc} alt="Escrituria Logo" className="h-20 md:h-28 w-auto object-contain" />
      </div>
      
      {/* Quick Access Section */}
      <div className="w-full max-w-4xl mx-auto mb-6 md:mb-10">
        <h2 className="text-lg md:text-xl font-semibold text-foreground mb-3 md:mb-4">Acesso Rápido</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
            {quickAccessAgents.map((agent) => (
                <button 
                    key={agent.id}
                    onClick={() => onSelectAgent(agent.id)}
                    className="p-3 md:p-4 bg-card border border-border rounded-lg hover:bg-secondary/5 hover:border-secondary active:scale-95 transition-all duration-300 shadow-sm hover:shadow-lg flex flex-col items-center text-center lg:flex-row lg:items-start lg:text-left min-h-[80px] touch-manipulation group"
                >
                    <div className="w-9 h-9 rounded-lg bg-secondary/10 flex items-center justify-center group-hover:bg-secondary/30 transition-colors flex-shrink-0 mb-2 lg:mb-0 lg:mr-3 lg:mt-1">
                      <div className="text-secondary">{agent.icon}</div>
                    </div>
                    <div>
                        <h3 className="text-sm md:text-base font-semibold text-foreground">{agent.name}</h3>
                        <p className="text-xs md:text-sm text-muted-foreground mt-1">{agent.description}</p>
                    </div>
                </button>
            ))}
        </div>
      </div>
      
      {/* Disclaimer Section */}
      <div className="mt-auto p-3 md:p-4 bg-card border border-border rounded-lg shadow-sm w-full">
        <p className="text-sm md:text-base text-muted-foreground font-serif leading-relaxed">
          <strong className="text-base md:text-lg font-semibold block mb-1 text-secondary">Lembre-se:</strong>
          <strong className="text-foreground">Nada substitui a devoção, a oração, a meditação profunda na Palavra e a direção do Espírito do Senhor.</strong>
        </p>
      </div>
      
    </div>
  );
};

export default WelcomeView;
